import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, CheckCircle, ArrowRight } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";

import { useUserAuth } from "../services/user/UserAuthServices";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPasswordAPI, loading, error, clearError } = useUserAuth();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return;
    }

    if (password.length < 8) {
      return;
    }

    const result = await resetPasswordAPI(email, token, password);
    if (result.status === "success") {
      setIsSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212] flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center p-4 py-12 md:py-20 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#62397f]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="w-full max-w-md relative animate-fade-in">
          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#62397f] to-[#7C3AED] flex items-center justify-center shadow-xl mx-auto mb-4 transform hover:scale-105 transition-transform duration-300">
              <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" className="w-16 h-16 object-contain" />
            </div>
            <h1 className="text-3xl font-medium font-serif text-[#111111] dark:text-white mb-2">
              Reset Password
            </h1>
            {!isSuccess && (
              <p className="text-[#666666] dark:text-[#A0A0A0]">
                Setting a new password for <span className="text-[#62397f] dark:text-[#A78BFA] font-medium">{email}</span>
              </p>
            )}
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-8 md:p-10">
            {isSuccess ? (
              <div className="text-center space-y-6 py-4 animate-fade-in">
                <div className="w-20 h-20 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-100 dark:border-green-800">
                  <CheckCircle className="text-green-600 dark:text-green-400" size={40} />
                </div>
                <div>
                  <h2 className="text-2xl font-medium font-serif text-[#111111] dark:text-white mb-3">Successfully Reset!</h2>
                  <p className="text-[#666666] dark:text-[#A0A0A0] text-base leading-relaxed">
                    Your password has been securely updated. You can now use your new password to sign in.
                  </p>
                </div>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 w-full py-4 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Go to Login <ArrowRight size={18} />
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in text-center">
                    <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {/* New Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#111111] dark:text-white ml-1">
                    New Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-[#111111] dark:text-white placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 focus:border-[#62397f] transition-all"
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#62397f] transition-colors p-1"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#111111] dark:text-white ml-1">
                    Confirm New Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-[#111111] dark:text-white placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 focus:border-[#62397f] transition-all"
                      placeholder="Repeat your password"
                      required
                    />
                  </div>
                </div>

                <div className="px-1">
                  <p className="text-[11px] text-[#666666] dark:text-[#A0A0A0] leading-relaxed">
                    Make sure your new password is secure and not used elsewhere. Use a combination of letters, numbers, and symbols.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none"
                >
                  {loading ? "Updating Password..." : "Reset Password"}
                </button>

                <Link
                  to="/login"
                  className="block text-center text-[#666666] dark:text-[#A0A0A0] text-sm hover:text-[#62397f] font-medium transition-colors"
                >
                  Cancel and return to login
                </Link>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
      `}</style>
    </div>
  );
}
